import {
  Alert,
  Badge,
  Box,
  ColorInput,
  Divider,
  Group,
  Paper,
  Slider,
  Stack,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import React from 'react';

import { useVectorCadStore } from '../stores/vector-cad-store';

export function WeldScorePanel() {
  const { weldScoreConfig, updateWeldScoreConfig } = useVectorCadStore();

  return (
    <Stack gap='md'>
      <Group justify='space-between'>
        <Group gap='xs'>
          <Title order={5}>Weld & Score Modifier</Title>
          <Badge size='xs' variant='gradient' gradient={{ from: 'blue', to: 'cyan' }}>
            Laser CAD
          </Badge>
        </Group>
      </Group>

      <Alert icon={<IconInfoCircle size={16} />} color='blue' variant='light' p='xs'>
        <Text size='xs'>
          <b>Weld & Score</b> joins overlapping letters into a single cuttable piece while
          preserving internal overlap lines as score paths for a layered 3D pop-out look.
        </Text>
      </Alert>

      {/* 1. Primary Weld Toggle */}
      <Paper withBorder p='sm' radius='md' style={{ background: 'var(--mantine-color-dark-7)' }}>
        <Stack gap='xs'>
          <Group justify='space-between'>
            <div>
              <Text size='sm' fw={600}>
                Weld Letters (Boolean Union)
              </Text>
              <Text size='xs' c='dimmed'>
                Merges overlapping outlines into one single outer shape
              </Text>
            </div>
            <Switch
              checked={weldScoreConfig.weldEnabled}
              onChange={(e) => updateWeldScoreConfig({ weldEnabled: e.currentTarget.checked })}
              color='red'
            />
          </Group>
        </Stack>
      </Paper>

      {/* 2. Inner Score Lines Toggle */}
      <Paper withBorder p='sm' radius='md' style={{ background: 'var(--mantine-color-dark-7)' }}>
        <Stack gap='xs'>
          <Group justify='space-between'>
            <div>
              <Text size='sm' fw={600}>
                Inner Overlap Score Lines
              </Text>
              <Text size='xs' c='dimmed'>
                Scores hidden letter edges for maximum legibility and depth
              </Text>
            </div>
            <Switch
              disabled={!weldScoreConfig.weldEnabled}
              checked={weldScoreConfig.scoreEnabled}
              onChange={(e) => updateWeldScoreConfig({ scoreEnabled: e.currentTarget.checked })}
              color='blue'
            />
          </Group>

          {weldScoreConfig.scoreEnabled && (
            <>
              <Divider my={4} />
              <Box>
                <Group justify='space-between' mb={2}>
                  <Text size='xs' c='dimmed'>
                    Min Score Segment Filter
                  </Text>
                  <Text size='xs' fw={600}>
                    {weldScoreConfig.minScoreLengthMm} mm
                  </Text>
                </Group>
                <Slider
                  size='xs'
                  min={0.1}
                  max={2.0}
                  step={0.1}
                  value={weldScoreConfig.minScoreLengthMm}
                  onChange={(val) => updateWeldScoreConfig({ minScoreLengthMm: val })}
                />
                <Text size='10px' c='dimmed' mt={2}>
                  Prevents laser from burning micro-dots at acute intersections.
                </Text>
              </Box>
            </>
          )}
        </Stack>
      </Paper>

      {/* 3. Floating Dot & Accent Auto-Bridge */}
      <Paper withBorder p='sm' radius='md' style={{ background: 'var(--mantine-color-dark-7)' }}>
        <Stack gap='xs'>
          <Group justify='space-between'>
            <div>
              <Text size='sm' fw={600}>
                Connect 'i' Dots & Accents
              </Text>
              <Text size='xs' c='dimmed'>
                Bridges loose dots so they don't fall out when cut
              </Text>
            </div>
            <Switch
              checked={weldScoreConfig.connectDots}
              onChange={(e) => updateWeldScoreConfig({ connectDots: e.currentTarget.checked })}
              color='teal'
            />
          </Group>

          {weldScoreConfig.connectDots && (
            <>
              <Divider my={4} />
              <Box>
                <Group justify='space-between' mb={2}>
                  <Text size='xs' c='dimmed'>
                    Bridge Thickness
                  </Text>
                  <Text size='xs' fw={600}>
                    {weldScoreConfig.bridgeWidthMm} mm
                  </Text>
                </Group>
                <Slider
                  size='xs'
                  min={0.8}
                  max={4.0}
                  step={0.1}
                  value={weldScoreConfig.bridgeWidthMm}
                  onChange={(val) => updateWeldScoreConfig({ bridgeWidthMm: val })}
                />
              </Box>
            </>
          )}
        </Stack>
      </Paper>

      {/* 4. Color & Laser Layer Settings */}
      <Paper withBorder p='sm' radius='md' style={{ background: 'var(--mantine-color-dark-7)' }}>
        <Stack gap='xs'>
          <Text size='xs' fw={600} tt='uppercase' c='dimmed'>
            Laser Vector Layers
          </Text>

          <Group grow>
            <ColorInput
              size='xs'
              label='Cut Line Color'
              value={weldScoreConfig.cutColor}
              onChange={(val) => updateWeldScoreConfig({ cutColor: val })}
            />
            <ColorInput
              size='xs'
              label='Score Line Color'
              value={weldScoreConfig.scoreColor}
              onChange={(val) => updateWeldScoreConfig({ scoreColor: val })}
            />
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
